import { Checkbox, TreeNodeModel } from '@creditsights/styleguide-web';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Icon, Input } from 'semantic-ui-react';

import { useFetchIssuers } from '../../hooks/issuers';
import styles from './CompanySpecificFilter.module.scss';

interface FilterProps {
  placeholder: string;
  dataTestId?: string;
  onChange: (checkedKeys: string[]) => void;
  checkedKeys: string[];
}
const cx = classNames.bind(styles);

const CompanySpecificFilter = ({ placeholder, dataTestId, onChange, checkedKeys }: FilterProps): React.ReactElement => {
  const [searchTerm, setSearchTerm] = useState('');
  const { data: fetchedIssuerFilters } = useFetchIssuers({ take: 100, text: searchTerm });
  const [transformedData, setTransformedData] = useState<TreeNodeModel[]>([]);
  const [originalTransformedData, setOriginalTransformedData] = useState<TreeNodeModel[]>([]);
  const [filtersMatchingSearch, setFiltersMatchingSearch] = useState<boolean>(true);

  useEffect(() => {
    if (fetchedIssuerFilters) {
      const newData = fetchedIssuerFilters.results
        .map((item) => ({
          key: item.tagId,
          title: item.displayName,
          children: undefined,
        }))
        .sort((a, b) => a.title.localeCompare(b.title));
      setTransformedData(newData);
      setFiltersMatchingSearch(newData.length > 0);
      if (!searchTerm) {
        setOriginalTransformedData(newData);
      }
    }
  }, [fetchedIssuerFilters, searchTerm]);

  const handleCheckboxChange = (key: string) => {
    const updatedCheckedKeys = checkedKeys.includes(key)
      ? checkedKeys.filter((item) => item !== key)
      : [...checkedKeys, key];
    onChange(updatedCheckedKeys);
  };

  const renderNoData = () => {
    if (!filtersMatchingSearch && searchTerm.trim().length > 0) {
      return (
        <div className={styles['no-results']} data-testid="filter-tree-no-results">
          No filters matching
          <br />
          &quot;{searchTerm}&quot;
        </div>
      );
    }
    return null;
  };

  const renderTransformedData = () => {
    if (searchTerm && transformedData.length === 0) {
      return null;
    }
    const dataToRender = searchTerm ? transformedData : originalTransformedData;
    return dataToRender.map((item) => (
      <div className={styles['checkbox-item']} key={item.key}>
        <Checkbox
          className={styles['custom-checkbox']}
          dataTestId="check-box"
          checked={checkedKeys.includes(item.key)}
          label={item.title}
          onChange={() => handleCheckboxChange(item.key)}
        />
      </div>
    ));
  };
  const heading = checkedKeys.length > 1 ? 'Companies' : 'Company';
  return (
    <div className={cx('body')} data-testid={dataTestId}>
      <div className={styles['filter-name']} data-testid="filter-name">
        {placeholder} {heading}
      </div>
      <div className={styles['filter-wrapper']}>
        <div style={{ paddingTop: '10px', paddingBottom: '10px' }}>
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search"
            className={styles['filter-input']}
            fluid
            icon={<Icon name="search" />}
            iconPosition="left"
          />
        </div>
        <div className={styles['scrollable-container']}>
          {renderNoData()}
          {renderTransformedData()}
        </div>
      </div>
    </div>
  );
};

export default CompanySpecificFilter;
